import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { type CommunicationChannelCacheRecord } from '@/pages/communicationChannel/communicationChannel.queries';
import { type ContactCacheRecord } from '@/pages/contact/contact.queries';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { useIndexedDB } from './useIndexedDB';
import { useOrganization } from './useOrganization';
import useRealTimeMessaging from './useRealtimeMessaging';
import { useUser } from './useUser';

/**
 * Listens for contact / communication-channel list invalidation via
 * (1) BroadcastChannel (same browser, Angular or React), (2) Deepstream events from the
 * server after mutations, and (3) `deepstreamReconnected` to avoid stale data after an offline
 * period.
 */
const BROADCAST_CHANNEL_NAME = 'contact-cache';
const INVALIDATE_CONTACTS_MSG = 'invalidate-contacts';
const INVALIDATE_CHANNELS_MSG = 'invalidate-communication-channels';
const INVALIDATE_ALL_MSG = 'invalidate-all';

const useContactCacheInvalidation = (): void => {
    const queryClient = useQueryClient();
    const { user } = useUser();
    const { organization } = useOrganization();
    const contactsIdb = useIndexedDB<ContactCacheRecord>('contacts');
    const channelsIdb = useIndexedDB<CommunicationChannelCacheRecord>('communicationChannels');

    const cacheKey = useCallback((): [string, string] | null => {
        if (user?.userId == null || organization?.id == null) {
            return null;
        }
        return [String(user.userId), String(organization.id)];
    }, [user, organization]);

    const deleteContactsIdb = useCallback(async () => {
        const key = cacheKey();
        if (key) {
            await contactsIdb.delete(key);
        }
    }, [cacheKey, contactsIdb]);

    const deleteChannelsIdb = useCallback(async () => {
        const key = cacheKey();
        if (key) {
            await channelsIdb.delete(key);
        }
    }, [cacheKey, channelsIdb]);

    const deleteBothIdb = useCallback(async () => {
        await deleteContactsIdb();
        await deleteChannelsIdb();
    }, [deleteChannelsIdb, deleteContactsIdb]);

    const onContactsPush = useCallback(async () => {
        await deleteContactsIdb();
        queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }, [deleteContactsIdb, queryClient]);

    const onChannelsPush = useCallback(async () => {
        await deleteChannelsIdb();
        queryClient.invalidateQueries({ queryKey: ['communicationchannels'] });
    }, [deleteChannelsIdb, queryClient]);

    const onAllPush = useCallback(async () => {
        await deleteBothIdb();
        queryClient.invalidateQueries({ queryKey: ['contacts'] });
        queryClient.invalidateQueries({ queryKey: ['communicationchannels'] });
    }, [deleteBothIdb, queryClient]);

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.CONTACTS_CHANGED_EVENT,
        eventHandler: onContactsPush,
    });

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.COMMUNICATION_CHANNELS_CHANGED_EVENT,
        eventHandler: onChannelsPush,
    });

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
            return;
        }

        const channel = new window.BroadcastChannel(BROADCAST_CHANNEL_NAME);

        channel.onmessage = (event: MessageEvent) => {
            const payload = event && event.data ? event.data : {};
            if (!payload || !payload.type) {
                return;
            }
            switch (payload.type) {
                case INVALIDATE_CONTACTS_MSG:
                    onContactsPush();
                    break;
                case INVALIDATE_CHANNELS_MSG:
                    onChannelsPush();
                    break;
                case INVALIDATE_ALL_MSG:
                    onAllPush();
                    break;
                default:
                    logger.debug('useContactCacheInvalidation: ignoring unknown message', payload);
            }
        };

        return () => {
            channel.onmessage = null;
            channel.close();
        };
    }, [onAllPush, onChannelsPush, onContactsPush]);

    useEffect(() => {
        const angularJSInjector = getInjector();
        const $rootScope = getRootScope(angularJSInjector);
        const deregister = $rootScope.$on('deepstreamReconnected', onAllPush);
        return () => {
            deregister();
        };
    }, [onAllPush]);
};

export { useContactCacheInvalidation };
